<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      Control who can operate this MLS ingest console — feeds, schedules, jobs, and destinations.
      This is for Brokurz operators, not end customers.
    </p>

    <Message v-if="!isSuperAdmin" severity="warn" :closable="false">
      Only the super admin (<strong>{{ superAdminEmail }}</strong>) can manage operators.
    </Message>

    <template v-else>
      <div class="kpi-row tw-mb-4">
        <div class="kpi-card">
          <div class="kpi-label">Operators</div>
          <div class="kpi-value">{{ users.length }}</div>
          <div class="kpi-hint">People who can open this console</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Active</div>
          <div class="kpi-value">{{ activeCount }}</div>
          <div class="kpi-hint">Accepted invite &amp; signed in</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">Pending</div>
          <div class="kpi-value">{{ pendingCount }}</div>
          <div class="kpi-hint">Invite sent, password not set</div>
        </div>
      </div>

      <Card class="tw-mb-4">
        <template #title>What operators can do</template>
        <template #content>
          <div class="access-grid">
            <div class="access-item">
              <i class="pi pi-home" />
              <div>
                <div class="access-title">Dashboard &amp; history</div>
                <div class="access-copy">Watch sync volume and feed health</div>
              </div>
            </div>
            <div class="access-item">
              <i class="pi pi-briefcase" />
              <div>
                <div class="access-title">Jobs &amp; alerts</div>
                <div class="access-copy">Run / cancel sync, purge, reconcile</div>
              </div>
            </div>
            <div class="access-item">
              <i class="pi pi-cog" />
              <div>
                <div class="access-title">Config</div>
                <div class="access-copy">MLS feeds, auth, destinations, rate limits</div>
              </div>
            </div>
            <div class="access-item access-item--locked">
              <i class="pi pi-users" />
              <div>
                <div class="access-title">Admins</div>
                <div class="access-copy">Invite / delete — super admin only ({{ superAdminEmail }})</div>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <Card class="tw-mb-4">
        <template #title>Invite an operator</template>
        <template #subtitle>
          Emails a set-password link. Invite people who will manage MLS ingest — not listing consumers.
        </template>
        <template #content>
          <form class="invite-row" @submit.prevent="invite">
            <div class="tw-flex-1" style="min-width: 220px">
              <label class="field-label">Work email</label>
              <InputText
                v-model="inviteEmail"
                type="email"
                placeholder="ops@brokurz.com"
                class="tw-w-full"
                required
              />
            </div>
            <Button
              type="submit"
              label="Send invite"
              icon="pi pi-send"
              class="tw-self-end"
              :loading="inviting"
            />
          </form>
          <p class="invite-note">
            After they accept, they can use Dashboard, Jobs, Schedules, Config, and Sources — same as you, except Admins.
          </p>
          <Message
            v-if="inviteMessage"
            :severity="inviteOk ? 'success' : 'error'"
            :closable="true"
            class="tw-mt-3"
            @close="inviteMessage = null"
          >
            {{ inviteMessage }}
          </Message>
        </template>
      </Card>

      <Card>
        <template #title>Operator directory</template>
        <template #subtitle>Super admin account is locked and cannot be deleted</template>
        <template #content>
          <div class="tw-mb-3 tw-flex tw-justify-end">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              class="p-button-outlined p-button-sm"
              :loading="loading"
              @click="load"
            />
          </div>
          <Message v-if="error" severity="error" :closable="false" class="tw-mb-3">{{ error }}</Message>

          <div v-if="!users.length && !loading" class="empty-panel">
            <div class="empty-icon"><i class="pi pi-users" /></div>
            <div class="empty-title">No operators yet</div>
            <p class="empty-copy">
              Invite a teammate who will configure MLS feeds and watch sync jobs. You remain the only super admin.
            </p>
          </div>

          <div v-else class="page-table">
            <div class="page-head">
              <span>Operator</span>
              <span>Role</span>
              <span>Status</span>
              <span>Last sign-in</span>
              <span />
            </div>
            <div v-for="u in sortedUsers" :key="u.id" class="page-row">
              <div>
                <div class="tw-font-semibold">{{ u.email }}</div>
                <div v-if="isPrimarySuper(u)" class="row-meta">Primary super admin</div>
              </div>
              <Chip :label="roleLabel(u)" :class="roleChipClass(u)" />
              <Tag :value="statusLabel(u)" :severity="statusSeverity(u)" />
              <span class="tw-text-sm">
                <display-datetime v-if="u.lastSignInAt" :datetime="u.lastSignInAt" />
                <span v-else class="tw-text-muted">Never</span>
              </span>
              <div class="tw-flex tw-gap-1 tw-justify-end tw-flex-wrap">
                <Button
                  v-if="!isPrimarySuper(u)"
                  :label="u.emailConfirmedAt ? 'Reset pw' : 'Resend'"
                  icon="pi pi-envelope"
                  class="p-button-outlined p-button-sm"
                  :loading="resendingEmail === u.email"
                  v-tooltip.top="u.emailConfirmedAt ? 'Send password reset email' : 'Resend invite email'"
                  @click="resend(u)"
                />
                <Button
                  v-if="!isPrimarySuper(u)"
                  label="Remove"
                  icon="pi pi-trash"
                  class="p-button-outlined p-button-danger p-button-sm"
                  :loading="deletingId === u.id"
                  @click="remove(u)"
                />
              </div>
            </div>
          </div>
        </template>
      </Card>
    </template>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { authState } from '../lib/auth'

const SUPER = (process.env.VUE_APP_SUPER_ADMIN_EMAIL || 'support@brokurz.com').toLowerCase()

export default {
  data() {
    return {
      users: [],
      loading: false,
      inviting: false,
      inviteEmail: '',
      inviteMessage: null,
      inviteOk: false,
      error: null,
      deletingId: null,
      resendingEmail: null,
      superAdminEmail: SUPER,
    }
  },
  computed: {
    isSuperAdmin() {
      return authState.isSuperAdmin
    },
    activeCount() {
      return this.users.filter(u => u.emailConfirmedAt).length
    },
    pendingCount() {
      return this.users.filter(u => !u.emailConfirmedAt).length
    },
    sortedUsers() {
      return [...this.users].sort((a, b) => {
        const as = this.isPrimarySuper(a) ? 0 : 1
        const bs = this.isPrimarySuper(b) ? 0 : 1
        if (as !== bs) return as - bs
        return String(a.email).localeCompare(String(b.email))
      })
    },
  },
  mounted() {
    if (this.isSuperAdmin) this.load()
  },
  methods: {
    isPrimarySuper(u) {
      return String(u.email || '').toLowerCase() === this.superAdminEmail
    },
    roleLabel(u) {
      if (this.isPrimarySuper(u) || u.role === 'superadmin') return 'superadmin'
      return 'admin'
    },
    roleChipClass(u) {
      return this.roleLabel(u) === 'superadmin' ? 'chip-super' : ''
    },
    statusLabel(u) {
      if (u.emailConfirmedAt) return 'Active'
      if (u.invitedAt) return 'Pending invite'
      return 'Pending'
    },
    statusSeverity(u) {
      if (u.emailConfirmedAt) return 'success'
      return 'warning'
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await this.$apollo.query({
          query: gql`query {
            adminUsers {
              id
              email
              role
              createdAt
              lastSignInAt
              invitedAt
              emailConfirmedAt
            }
          }`,
          fetchPolicy: 'network-only',
        })
        this.users = data.adminUsers || []
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    async invite() {
      this.inviting = true
      this.inviteMessage = null
      try {
        const email = this.inviteEmail.trim()
        await this.$apollo.mutate({
          mutation: gql`mutation ($email: String!) {
            inviteAdmin(email: $email) {
              id
              email
              role
            }
          }`,
          variables: { email },
        })
        this.inviteOk = true
        this.inviteMessage = `Invite sent to ${email}. They’ll set a password from the email link, then can operate ingest.`
        this.inviteEmail = ''
        await this.load()
      } catch (err) {
        this.inviteOk = false
        this.inviteMessage = err.message
      } finally {
        this.inviting = false
      }
    },
    async resend(user) {
      this.resendingEmail = user.email
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($email: String!) {
            resendAdminInvite(email: $email)
          }`,
          variables: { email: user.email },
        })
        this.$toast.add({
          severity: 'success',
          summary: user.emailConfirmedAt ? 'Reset email sent' : 'Invite resent',
          detail: user.email,
          life: 3000,
        })
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Could not resend',
          detail: err.message,
          life: 5000,
        })
      } finally {
        this.resendingEmail = null
      }
    },
    async remove(user) {
      const ok = window.confirm(
        `Remove ${user.email}?\n\nThey will immediately lose access to the MLS ingest console (config, jobs, feeds).`
      )
      if (!ok) return
      this.deletingId = user.id
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($userId: String!) {
            deleteAdmin(userId: $userId)
          }`,
          variables: { userId: user.id },
        })
        this.$toast.add({ severity: 'success', summary: 'Operator removed', life: 2500 })
        await this.load()
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Remove failed',
          detail: err.message,
          life: 5000,
        })
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.kpi-card {
  padding: 1rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.kpi-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-top: 0.15rem;
  color: #18181b;
}

.kpi-hint {
  font-size: 0.75rem;
  color: #a1a1aa;
  margin-top: 0.2rem;
}

.access-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.access-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.85rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fafafa;
}

.access-item i {
  margin-top: 0.15rem;
  color: #52525b;
}

.access-item--locked {
  border-style: dashed;
}

.access-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #18181b;
}

.access-copy {
  font-size: 0.8rem;
  color: #71717a;
  margin-top: 0.15rem;
  line-height: 1.4;
}

.invite-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: flex-end;
}

.invite-note {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
  color: #71717a;
  line-height: 1.4;
}

.page-table {
  display: flex;
  flex-direction: column;
}

.page-head,
.page-row {
  display: grid;
  grid-template-columns: 1.5fr 0.8fr 0.9fr 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0;
  border-bottom: 1px solid #f4f4f5;
  font-size: 0.875rem;
}

.page-head {
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom-color: #e4e4e7;
}

.row-meta {
  font-size: 0.72rem;
  color: #a1a1aa;
  margin-top: 0.15rem;
}

.empty-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
  border: 1px dashed #e4e4e7;
  border-radius: 8px;
  background: #fafafa;
}

.empty-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  border: 1px solid #e4e4e7;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #52525b;
}

.empty-title {
  font-weight: 700;
  color: #18181b;
}

.empty-copy {
  max-width: 26rem;
  margin: 0.4rem 0 0;
  font-size: 0.875rem;
  color: #71717a;
  line-height: 1.45;
}

::v-deep .chip-super {
  background: #18181b !important;
  color: #fff !important;
  border-color: #18181b !important;
}

@media (max-width: 900px) {
  .kpi-row,
  .access-grid {
    grid-template-columns: 1fr;
  }

  .page-head {
    display: none;
  }

  .page-row {
    grid-template-columns: 1fr;
  }
}
</style>
